import type {
  AssetBodyDto,
  DiagnosticProcedureBodyDto,
  ReportBodyDto,
  ReportResponseDto,
} from '@/api/WhisApi/generated';
import { AssetBodyDtoType, ReportBodyDtoStatus } from '@/api/WhisApi/generated';

const getDefinedValue = <T, K extends string>(
  key: K,
  value: T | undefined,
): Partial<Record<K, T>> =>
  value !== undefined ? ({ [key]: value } as Record<K, T>) : {};

const buildReportStatusUpdatePayload = (
  report: ReportResponseDto,
  status: ReportBodyDto['status'],
): ReportBodyDto => ({
  code: report.code,
  status,
  ...(report.animalType && {
    animalType: report.animalType as ReportBodyDto['animalType'],
  }),
  ...(report.specie && { specie: report.specie }),
  ...(report.wildType && {
    wildType: report.wildType as ReportBodyDto['wildType'],
  }),
  ...getDefinedValue('latitude', report.latitude),
  ...getDefinedValue('longitude', report.longitude),
  ...(typeof report.inaccurateLocation === 'boolean' && {
    inaccurateLocation: report.inaccurateLocation,
  }),
  ...(report.detectionDate && { detectionDate: report.detectionDate }),
  ...(report.surveillanceType && {
    surveillanceType:
      report.surveillanceType as ReportBodyDto['surveillanceType'],
  }),
  ...(report.description && { description: report.description }),
  ...getDefinedValue('numberOfDeaths', report.numberOfDeaths),
  ...getDefinedValue('numberOfSicks', report.numberOfSicks),
  ...getDefinedValue('populationSize', report.populationSize),
  ...(report.humanAffected && {
    humanAffected: report.humanAffected as ReportBodyDto['humanAffected'],
  }),
  ...(report.domesticAnimalAffected && {
    domesticAnimalAffected:
      report.domesticAnimalAffected as ReportBodyDto['domesticAnimalAffected'],
  }),
  ...(typeof report.knownCause === 'boolean' && {
    knownCause: report.knownCause,
  }),
  ...(report.order && { order: report.order }),
  ...(report.etiologyType?.code && {
    etiologyTypeCode: report.etiologyType.code,
  }),
  ...(report.etiologicalAgent?.code && {
    etiologicalAgentCode: report.etiologicalAgent.code,
  }),
  ...(report.diagnosticProcedures.length > 0 && {
    diagnosticProcedures: report.diagnosticProcedures.map((procedure) => ({
      ...(procedure.categoryOfTest && {
        categoryOfTest:
          procedure.categoryOfTest as DiagnosticProcedureBodyDto['categoryOfTest'],
      }),
      ...(procedure.extraTestInformation && {
        extraTestInformation: procedure.extraTestInformation,
      }),
      ...getDefinedValue(
        'numberOfPositiveTests',
        procedure.numberOfPositiveTests,
      ),
      ...getDefinedValue(
        'numberOfNegativeTests',
        procedure.numberOfNegativeTests,
      ),
      ...getDefinedValue('numberOfTests', procedure.numberOfTests),
      ...(procedure.sampleType?.code && {
        sampleTypeCode: procedure.sampleType.code,
      }),
      ...(procedure.subCategory?.code && {
        subCategoryCode: procedure.subCategory.code,
      }),
      ...(procedure.family?.code && { familyCode: procedure.family.code }),
    })),
  }),
  ...(report.assets.length > 0 && {
    assets: report.assets.map((asset) => ({
      name: asset.name,
      fileName: asset.fileName,
      type: asset.type ?? AssetBodyDtoType.supportingDocument,
    })),
  }),
});

export const buildUploadRraPayload = (
  report: ReportResponseDto,
  rraAsset: AssetBodyDto,
  versionNote?: string,
): ReportBodyDto => {
  const payload = buildReportStatusUpdatePayload(
    report,
    ReportBodyDtoStatus.publishedRRA,
  );
  const assetsWithoutRra = (payload.assets ?? []).filter(
    (asset) => asset.type !== AssetBodyDtoType.rra,
  );

  return {
    ...payload,
    ...(versionNote && { versionNote }),
    assets: [...assetsWithoutRra, rraAsset],
  };
};
